/**
 * 「最近作品 · 查看全部 →」横向带(F1-b)。
 *
 * ═══ 它替掉了什么 ═════════════════════════════════════════════════════════════
 * 三个 hub 页原本各挂一整套「进行中 / 草稿 / 历史(状态 chips + 场景 chips + 搜索 + 分页 +
 * 批量下载)」。那是**检索**,不是**选择** —— 与 hub 的职责(这个域能做什么、我选哪个)
 * 撞在一页里。检索整体搬进 `/works`,hub 只留这一条带:一眼看到「我最近做出了什么」,
 * 想看全部点「查看全部 →」。
 *
 * ═══ 为什么带上不给操作 ═══════════════════════════════════════════════════════
 * 这条带是**回看**不是**管理**:重命名/删除/重试全在 `/works`。带里放操作会诱导用户
 * 在一个只显示 6 条的地方做批量管理的事,而且删完这条带自己会跳动。
 *
 * 空态:整条不渲染(不占位、不留一句「暂无作品」的白条)—— 新租户的 hub 应该只有卡墙。
 */
import { useQueries } from "@tanstack/react-query";
import { ArrowRight, FolderOpen } from "lucide-react";

import { cn } from "@/shared/lib/utils";
import { WorkCard } from "./WorkCard";
import {
  RECENT_SIZE,
  isLineWired,
  listRecentWorks,
  type RecentWorksFilter,
} from "../api/recentWorksApi";
import { mergeWorks } from "../model/mappers";
import type { WorkItem, WorkLineKey } from "../model/types";

export interface RecentWorksRowProps {
  /**
   * 取哪几条线。hub 页传自己那一条;工作台传「当前用户可见的全部线」——
   * 多线时并行拉各线 top6 再 k 路归并取 6(与 `/works` 同一套归并,不另写一份)。
   */
  lines: WorkLineKey[];
  /** 点卡做什么:各 hub 传自己的预览器(列表=预览,详情=编辑);不传则整卡不可点。 */
  onOpen?: (item: WorkItem) => void;
  /**
   * 线内子线过滤(分身三条线各自的工作台用)。★只在单线时有意义 —— 多线归并时
   * 各线的子线语义不同,一个谓词套不住,传了也只作用在每条线自己的结果上。
   */
  filter?: RecentWorksFilter;
  /** 标题;默认「最近作品」。子线带上写清是**哪条线**的,否则用户以为分身只出过这几条。 */
  title?: string;
  /** 「查看全部」落点;默认 `/works?line=<line>`。 */
  viewAllHref?: string;
  onViewAll?: (href: string) => void;
  className?: string;
}

export function RecentWorksRow({
  lines,
  onOpen,
  filter,
  title = "最近作品",
  viewAllHref,
  onViewAll,
  className,
}: RecentWorksRowProps) {
  const wired = lines.filter(isLineWired);
  const qs = useQueries({
    queries: wired.map((l) => ({
      // ★filter.key 必须进 queryKey:谓词是函数,不带 key 三条线会共用同一份缓存,
      //   谁先加载谁的结果就成了另外两条线看到的东西。
      queryKey: ["recent-works", l, filter?.key ?? null],
      queryFn: () => listRecentWorks(l, RECENT_SIZE, filter),
      // 失败静默:这条带是锦上添花,拉不到就整条不渲染。★retry:1 只挡瞬时抖动,
      // meta.suppressGlobalErrorToast 退订全局 QueryCache 的破坏性 toast —— 否则一个可选带
      // 拉失败会在页面顶上弹红条(codex review P2)。
      retry: 1,
      staleTime: 30_000,
      meta: { suppressGlobalErrorToast: true },
    })),
  });

  const items = mergeWorks(
    qs.map((q) => q.data ?? []),
    RECENT_SIZE
  );
  if (items.length === 0) return null;

  // 单线 → 直接落到该线的筛选;多线 → 落到全部作品
  const href = viewAllHref ?? (wired.length === 1 ? `/works?line=${wired[0]}` : "/works");

  return (
    <section data-testid="recent-works-row" className={cn("work-row", className)}>
      <div className="mb-3 flex items-center gap-2">
        <FolderOpen className="h-4 w-4 text-success" />
        <h2 className="text-sm font-semibold">{title}</h2>
        {/* ★紧挨标题、不推到最右(2026-07-24 H-A 门实测):军师悬浮球是 `position:fixed`
            的 56×56 圆钮,常驻视口右侧中部。原来「查看全部」贴右缘时与它重叠 48×20px,
            `elementFromPoint(按钮中心)` 命中的是悬浮球 —— 按钮中心根本点不到。
            贴着标题放同时也更符合就近原则:动作和它作用的那节标题在一起。
            触控:窄屏 min-h-11(44px),桌面 min-h-8。 */}
        <button
          type="button"
          onClick={() => onViewAll?.(href)}
          className={cn(
            "inline-flex min-h-8 items-center gap-0.5 rounded-full px-2 text-xs font-medium text-primary",
            "transition-colors hover:bg-primary/10 max-sm:min-h-11"
          )}
        >
          查看全部
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
      <div className="work-strip">
        {items.map((w) => (
          <WorkCard key={w.uid} item={w} onOpen={() => onOpen?.(w)} />
        ))}
      </div>
    </section>
  );
}
